import {
    BehaviorSubject,
    Observable,
    combineLatest,
    filter,
    map,
    take,
} from "rxjs";

import { REVIEWS_SORTING_TYPE } from "../constants";
import ReviewsSortingOptionsEntity from "../entities/ReviewsSortingOptionsEntity";
import ReviewsStorefrontValuesSettingsEntity from "../entities/ReviewsStorefrontValuesSettingsEntity";

type SortItem = {
    value: string;
    labelTranslationKey: string;
    labelTranslationNamespace: string;
};

export class SortingManager {
    private currentSortValue$ =
        new BehaviorSubject<ReviewsSortingOptionsEntity>(
            REVIEWS_SORTING_TYPE.MOST_RECENT
        );
    private currentSortItem$: Observable<SortItem>;
    private sortItems$: Observable<Array<SortItem>>;

    public constructor(
        private readonly reviewsSettings$: Observable<ReviewsStorefrontValuesSettingsEntity>,
        private readonly translationsNamespace: string
    ) {
        this.sortItems$ = this.reviewsSettings$.pipe(
            map((settings) => {
                const sortItems: SortItem[] = [
                    ...(settings?.isReviewsPinningEnabled
                        ? [
                              {
                                  value: REVIEWS_SORTING_TYPE.MOST_RELEVANT,
                                  labelTranslationKey: "sort_most_relevant",
                                  labelTranslationNamespace:
                                      this.translationsNamespace,
                              },
                          ]
                        : []),
                    {
                        value: REVIEWS_SORTING_TYPE.MOST_RECENT,
                        labelTranslationKey: "sort_most_recent",
                        labelTranslationNamespace: this.translationsNamespace,
                    },
                    {
                        value: REVIEWS_SORTING_TYPE.HIGHEST_RATING,
                        labelTranslationKey: "sort_highest_rating",
                        labelTranslationNamespace: this.translationsNamespace,
                    },
                    {
                        value: REVIEWS_SORTING_TYPE.LOWEST_RATING,
                        labelTranslationKey: "sort_lowest_rating",
                        labelTranslationNamespace: this.translationsNamespace,
                    },
                    {
                        value: REVIEWS_SORTING_TYPE.WITH_PHOTOS,
                        labelTranslationKey: "sort_with_photos",
                        labelTranslationNamespace: this.translationsNamespace,
                    },
                    {
                        value: REVIEWS_SORTING_TYPE.MOST_HELPFUL,
                        labelTranslationKey: "sort_most_helpful",
                        labelTranslationNamespace: this.translationsNamespace,
                    },
                ];

                return sortItems;
            })
        );

        this.reviewsSettings$.pipe(take(1)).subscribe((settings) => {
            if (
                settings.defaultSortingOption &&
                settings.defaultSortingOption !==
                    REVIEWS_SORTING_TYPE.MOST_RECENT
            ) {
                this.currentSortValue$.next(settings.defaultSortingOption);
            }
        });

        this.currentSortItem$ = combineLatest([
            this.sortItems$,
            this.currentSortValue$,
        ]).pipe(
            filter(([sortItems, currentSortValue]) =>
                sortItems.some((item) => item.value === currentSortValue)
            ),
            map(([sortItems, currentSortValue]) => {
                const item = sortItems.find(
                    (sortItem) => sortItem.value === currentSortValue
                );

                if (!item) {
                    throw new Error(`Invalid sort value: ${currentSortValue}`);
                }

                return item;
            })
        );
    }

    public selectCurrentSortValue(): Observable<ReviewsSortingOptionsEntity> {
        return this.currentSortValue$.asObservable();
    }

    public selectCurrentSortItem(): Observable<SortItem> {
        return this.currentSortItem$;
    }

    public selectSortItems(): Observable<Array<SortItem>> {
        return this.sortItems$;
    }

    public setCurrentSort(sortValue: string): void {
        this.currentSortValue$.next(sortValue as ReviewsSortingOptionsEntity);
    }
}
